
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Edit,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Trash2,
  KeyRound,
  UserCheck,
  UserX,
} from 'lucide-react';
import { UserWithMembership } from '@/hooks/useUsers';
import { useTranslation } from 'react-i18next';
import UserActions from './UserActions';

interface UserCardProps {
  user: UserWithMembership;
  onEdit: (user: UserWithMembership) => void;
  onDelete: (user: UserWithMembership) => void;
  onResetPassword: (user: UserWithMembership) => void;
  onToggleStatus: (user: UserWithMembership) => void;
  canDelete?: boolean;
  isTogglingStatus?: boolean;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  onEdit,
  onDelete,
  onResetPassword,
  onToggleStatus,
  canDelete = false,
  isTogglingStatus = false,
}) => {
  const { t } = useTranslation();

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <ShieldAlert className="h-4 w-4" />;
      case 'manager':
        return <ShieldCheck className="h-4 w-4" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'manager':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin':
        return t('users.admin');
      case 'manager':
        return t('users.manager');
      case 'staff':
        return t('users.user');
      default:
        return t('users.user');
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* User info */}
          <div>
            <h3 className="font-semibold text-lg">{user.full_name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge
                variant={getRoleBadgeVariant(user.role ?? 'staff')}
                className="flex items-center gap-1"
              >
                {getRoleIcon(user.role ?? 'staff')}
                {getRoleDisplayName(user.role ?? 'staff')}
              </Badge>
              <Badge
                variant={user.membership_is_active ? 'default' : 'destructive'}
              >
                {user.membership_is_active ? t('users.active') : t('users.disabled')}
              </Badge>
            </div>
          </div>

          {/* Additional info */}
          <div className="space-y-1 text-sm text-muted-foreground">
            <div>
              <span className="font-medium">{t('users.lastLogin')}:</span>{' '}
              {user.last_login_at
                ? new Date(user.last_login_at).toLocaleDateString()
                : t('users.never')}
            </div>
            <div>
              <span className="font-medium">{t('users.created')}:</span>{' '}
              {new Date(user.created_at).toLocaleDateString()}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-center gap-8 mt-[24px]">
            <UserActions
              user={user}
              onEdit={onEdit}
              onDelete={onDelete}
              onResetPassword={onResetPassword}
              onToggleStatus={onToggleStatus}
              canDelete={canDelete}
              isTogglingStatus={isTogglingStatus}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserCard;
